import type {ActivateEmploymentRequest, EmploymentsResponse, SelfEmploymentProvider} from '@rocket-ui/lk-api';
import {activateEmployment, fetchEmployment, updateEmployment} from '@rocket-ui/lk-api';
import type {RegistrationWorkStatus} from './types';
import {buildEmploymentRequest} from './workStatus';
import {getSelfEmploymentProvider, getSelfEmploymentSnapshot} from './selfEmploymentProfiles';

export type ActivateEmploymentWithCurrentOptions = {
	signal?: AbortSignal;
	/** Только СМЗ. Для физлица и ИП поле не отправляем. */
	provider?: SelfEmploymentProvider | null;
};

/**
 * Провайдер для activate и PUT — только у СМЗ.
 * Партнёр плитки (alfa, rocketwork, free_smz), иначе провайдер хоста.
 */
export const resolveEmploymentRequestProvider = (
	workStatus: RegistrationWorkStatus,
	partner?: SelfEmploymentProvider | null,
): SelfEmploymentProvider | undefined => {
	if (workStatus !== 'self_employment') {
		return undefined;
	}

	return partner ?? getSelfEmploymentProvider();
};

export const hasCurrentEmployment = (data: EmploymentsResponse): boolean => {
	return (
		data.individual?.current === true ||
		data.individualEntrepreneur?.current === true ||
		data.representativeOfTheCompany?.current === true ||
		getSelfEmploymentSnapshot(data)?.current === true
	);
};

/** `provider` только у СМЗ. Физлицо и ИП — `{kind}` без провайдера. */
const employmentBody = (workStatus: RegistrationWorkStatus, provider?: SelfEmploymentProvider | null) => {
	const request = buildEmploymentRequest(workStatus);

	if (workStatus !== 'self_employment' || !provider) {
		return request;
	}

	return {...request, provider};
};

/**
 * Подключение статуса на регистрации:
 * POST employments/activate → PUT employment (current), если current ещё не был выбран.
 * СМЗ не ставит current здесь: PUT с provider всегда шлёт «Продолжить» на зелёном экране.
 */
export const activateEmploymentWithCurrent = async (
	workStatus: RegistrationWorkStatus,
	options: ActivateEmploymentWithCurrentOptions,
): Promise<EmploymentsResponse> => {
	const request: ActivateEmploymentRequest = employmentBody(workStatus, options.provider);
	const {data: employmentBefore} = await fetchEmployment(options.signal);
	const hadCurrent = hasCurrentEmployment(employmentBefore);

	const {data: activated} = await activateEmployment(request, options.signal);

	if (hadCurrent || workStatus === 'self_employment') {
		return activated;
	}

	const {data: updated} = await updateEmployment(request, options.signal);

	return updated;
};

/**
 * Сделать уже подключённый статус основным.
 * POST /activate только включает active; current переключает PUT /employment.
 */
export const switchCurrentEmployment = async (
	workStatus: RegistrationWorkStatus,
	options: ActivateEmploymentWithCurrentOptions,
): Promise<EmploymentsResponse> => {
	const {data} = await updateEmployment(employmentBody(workStatus, options.provider), options.signal);

	return data;
};
